import { useMemo, useState } from 'react';
import { Alert, Badge, Button, Modal, ProgressBar, Table } from 'react-bootstrap';
import { useClientFlags } from '@/hooks/useClientFlags';
import { clientMetadataService } from '@/services/clientMetadataService';
import { ACCOUNT_ORG_DISPLAY } from '@/types';
import type { AccountOrgValue, Client } from '@/types';
import '@/components/clientFlags/clientFlags.css';

interface OrganisationReviewProps {
  clients: Client[];
  /** Each client's organisation as AWS gives it now. Null means AWS did not say. */
  orgMap: ReadonlyMap<string, AccountOrgValue | null>;
}

interface Change {
  name: string;
  saved?: AccountOrgValue;
  fromAws: AccountOrgValue;
}

interface Result {
  name: string;
  ok: boolean;
  detail?: string;
}

/**
 * The most clients whose saved organisation one save may change. More than
 * this at once means the AWS lookup is wrong far more often than the records:
 * on 2026-10-13 a throttled lookup relabelled 181 clients as standalone.
 */
const MAX_RELABELS_AT_ONCE = 5;
const WORKERS = 8;

const label = (org?: AccountOrgValue) => (org ? ACCOUNT_ORG_DISPLAY[org].label : 'Not saved');
const plural = (n: number, one: string, many: string) => `${n.toLocaleString()} ${n === 1 ? one : many}`;

/**
 * Says when AWS places a client in a different organisation from the saved
 * record, and saves the difference once a person has read it and pressed the
 * button. The page used to save this by itself each time it opened. A page
 * that writes on load turns one bad lookup into a table of wrong rows, so the
 * write now waits for a person.
 */
export function OrganisationReview({ clients, orgMap }: OrganisationReviewProps) {
  const flags = useClientFlags();
  const [open, setOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [results, setResults] = useState<Result[] | null>(null);
  // The list the dialog opened with. The saved records change under it as each write lands.
  const [held, setHeld] = useState<Change[] | null>(null);

  const changes = useMemo<Change[]>(() => {
    // Rows that were not read say nothing. Comparing against them would offer to rewrite every client.
    if (flags.status !== 'ready') return [];
    const rows: Change[] = [];
    for (const client of clients) {
      const fromAws = orgMap.get(client.name);
      if (!fromAws) continue;
      const saved = flags.metadata.get(client.name)?.accountOrg;
      if (saved !== fromAws) rows.push({ name: client.name, saved, fromAws });
    }
    return rows.sort((a, b) => a.name.localeCompare(b.name));
  }, [clients, orgMap, flags.status, flags.metadata]);

  const relabels = changes.filter((c) => c.saved);
  const tooMany = relabels.length > MAX_RELABELS_AT_ONCE;
  const savable = tooMany ? changes.filter((c) => !c.saved) : changes;
  const list = held ?? savable;

  if (changes.length === 0 && !open) return null;

  const save = async (targets: Change[], previous: Result[]) => {
    setRunning(true);
    setProgress({ done: 0, total: targets.length });
    const queue = [...targets];
    const fresh: Result[] = [];
    let done = 0;
    const worker = async () => {
      for (let change = queue.shift(); change; change = queue.shift()) {
        try {
          await clientMetadataService.updateFields(change.name, { accountOrg: change.fromAws });
          fresh.push({ name: change.name, ok: true });
        } catch (e) {
          fresh.push({ name: change.name, ok: false, detail: e instanceof Error ? e.message : String(e) });
        }
        done += 1;
        setProgress({ done, total: targets.length });
      }
    };
    await Promise.all(Array.from({ length: Math.min(WORKERS, targets.length) }, worker));
    const retried = new Set(targets.map((t) => t.name));
    setResults(
      [...previous.filter((r) => !retried.has(r.name)), ...fresh].sort((a, b) => a.name.localeCompare(b.name))
    );
    setRunning(false);
  };

  const openDialog = () => {
    setHeld(savable);
    setResults(null);
    setOpen(true);
  };
  const close = () => {
    setOpen(false);
    setHeld(null);
    setResults(null);
  };
  const failed = results?.filter((r) => !r.ok) ?? [];

  return (
    <>
      {changes.length > 0 && (
        <Alert variant={tooMany ? 'warning' : 'info'} className="d-flex align-items-center flex-wrap gap-2">
          <span className="flex-grow-1">
            {tooMany
              ? `AWS disagrees with the saved organisation of ${plural(relabels.length, 'client', 'clients')} at once. That many at once is more often a failed lookup than moved accounts, so those are left as they are.`
              : `AWS places ${plural(changes.length, 'client', 'clients')} in an organisation that is not on the saved record.`}
            {tooMany &&
              savable.length > 0 &&
              ` ${plural(savable.length, 'client has', 'clients have')} no saved organisation yet.`}
          </span>
          {savable.length > 0 && (
            <Button size="sm" variant="outline-secondary" onClick={openDialog}>
              Review and save
            </Button>
          )}
        </Alert>
      )}

      <Modal show={open} onHide={running ? undefined : close} centered>
        <Modal.Header closeButton={!running}>
          <Modal.Title>Save the organisation of {plural(list.length, 'client', 'clients')}?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!results ? (
            <>
              <p className="client-flag-dialog__effect">
                This saves what AWS says for each client below. The dashboard splits costs by organisation from the
                saved record. Nothing else on a client's record changes.
              </p>
              <div className="client-flag-dialog__list">
                <Table size="sm">
                  <thead>
                    <tr>
                      <th>Client</th>
                      <th>Saved now</th>
                      <th>From AWS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((change) => (
                      <tr key={change.name}>
                        <td className="client-flag-dialog__name">{change.name}</td>
                        <td>{label(change.saved)}</td>
                        <td>{label(change.fromAws)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              {running && (
                <div className="client-flag-dialog__progress">
                  <ProgressBar now={progress.total ? (100 * progress.done) / progress.total : 0} />
                  <span className="numeric">
                    {progress.done} of {progress.total} done
                  </span>
                </div>
              )}
            </>
          ) : (
            <>
              <p className="client-flag-dialog__outcome">
                {plural(results.filter((r) => r.ok).length, 'client', 'clients')} saved
                {failed.length > 0 && `, ${failed.length.toLocaleString()} failed`}.
              </p>
              <div className="client-flag-dialog__list">
                <Table size="sm">
                  <tbody>
                    {results.map((r) => (
                      <tr key={r.name}>
                        <td className="client-flag-dialog__name">{r.name}</td>
                        <td>{r.ok ? <Badge bg="success">Saved</Badge> : <Badge bg="danger">Failed</Badge>}</td>
                        <td className="client-flag-dialog__detail">{r.detail ?? ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          {!results ? (
            <>
              <Button variant="outline-secondary" onClick={close} disabled={running}>
                Cancel
              </Button>
              <Button variant="primary" onClick={() => void save(list, [])} disabled={running || list.length === 0}>
                {running ? 'Saving' : `Save ${plural(list.length, 'organisation', 'organisations')}`}
              </Button>
            </>
          ) : (
            <>
              {failed.length > 0 && (
                <Button
                  variant="outline-secondary"
                  disabled={running}
                  onClick={() =>
                    void save(
                      list.filter((change) => failed.some((f) => f.name === change.name)),
                      results
                    )
                  }
                >
                  Try the {failed.length.toLocaleString()} failed again
                </Button>
              )}
              <Button variant="primary" onClick={close} disabled={running}>
                Close
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default OrganisationReview;
